/**
 * What: Defines pure feature entitlement contracts and deterministic resolution.
 * Transport / data-flow: Pure TypeScript/Zod validation only; apps/web owns D1 reads and Shopify transport.
 * Algorithm or layout: Effective state is derived from code-owned feature metadata, global availability,
 * plan grants, scoped overrides, dependencies, and connection context.
 */
import * as z from 'zod';
export { combineCapabilityStatus, } from './capability-status.js';
export const shopifyFeatureKeys = [
    'synthetic_audience',
    'studies',
    'audience_studio',
    'shopper_intelligence',
    'analytics_sources',
    'pixel_events',
    'micro_surveys',
    'product_edit_apply',
    'merchant_questions',
    'integration_shopify',
    'integration_posthog',
    'integration_google_search_console',
    'integration_competitor_catalog',
    'integration_meta_ads',
    'market_discovery',
    'research_cards',
];
export const featureKeySchema = z.enum(shopifyFeatureKeys);
export const entitlementStateSchema = z.enum(['enabled', 'disabled', 'coming_soon', 'unavailable']);
export const entitlementSourceSchema = z.enum([
    'global',
    'plan',
    'override',
    'dependency',
    'missing_connection',
    'resolution_error',
]);
export const featureGlobalStateSchema = z.object({
    featureKey: featureKeySchema,
    state: z.enum(['enabled', 'disabled', 'coming_soon']),
    reason: z.string().min(1).nullable().optional(),
});
export const featurePlanGrantSchema = z.object({
    featureKey: featureKeySchema,
    grantState: z.enum(['included', 'excluded']),
});
export const featureOverrideSchema = z.object({
    featureKey: featureKeySchema,
    scopeType: z.enum(['org', 'shopify_shop']),
    scopeId: z.string().min(1),
    overrideState: z.enum(['enabled', 'disabled']),
    reason: z.string().min(1).nullable().optional(),
});
export const entitlementContextSchema = z.object({
    orgId: z.string().min(1).nullable(),
    productSurface: z.enum(['core_workspace', 'shopify_integration']),
    connectionStatus: z.enum(['active', 'missing']),
    shopDomain: z.string().min(1).nullable().optional(),
    connectionId: z.string().min(1).nullable().optional(),
});
const featureMetadata = {
    synthetic_audience: {
        key: 'synthetic_audience',
        label: 'Synthetic audience',
        requiresConnection: true,
        defaultGlobalState: 'enabled',
        defaultGlobalReason: 'Core Shopify synthetic-audience capability is generally available for eligible plans.',
        dependsOn: [],
    },
    studies: {
        key: 'studies',
        label: 'Studies',
        inheritsFrom: 'synthetic_audience',
        requiresConnection: true,
        defaultGlobalState: 'enabled',
        defaultGlobalReason: 'Studies inherit synthetic-audience access for eligible plans.',
        dependsOn: ['synthetic_audience'],
    },
    audience_studio: {
        key: 'audience_studio',
        label: 'Audience Studio',
        inheritsFrom: 'synthetic_audience',
        requiresConnection: true,
        defaultGlobalState: 'enabled',
        defaultGlobalReason: 'Audience Studio inherits synthetic-audience access for eligible plans.',
        dependsOn: ['synthetic_audience'],
    },
    shopper_intelligence: {
        key: 'shopper_intelligence',
        label: 'Shopper Intelligence',
        inheritsFrom: 'synthetic_audience',
        requiresConnection: true,
        defaultGlobalState: 'enabled',
        defaultGlobalReason: 'Shopper Intelligence inherits synthetic-audience access for eligible plans.',
        dependsOn: ['synthetic_audience'],
    },
    // Independent grant preserves rollout/add-on pricing control; dependency encodes the core capability requirement.
    analytics_sources: {
        key: 'analytics_sources',
        label: 'Analytics sources',
        requiresConnection: true,
        defaultGlobalState: 'enabled',
        defaultGlobalReason: 'Analytics source integrations are grantable for eligible plans.',
        dependsOn: ['synthetic_audience'],
    },
    pixel_events: {
        key: 'pixel_events',
        label: 'Pixel events',
        requiresConnection: true,
        defaultGlobalState: 'coming_soon',
        defaultGlobalReason: 'Pixel events are not generally available yet.',
        dependsOn: [],
    },
    /*
     * Gates merchant-authored study questions. Ships `coming_soon` deliberately: the deterministic
     * rubric catches literal blind-elicitation violations but not paraphrases, and merchant question
     * text shares one model turn with the scored trio. Flip to `enabled` when the classifier tier
     * lands, not before.
     */
    merchant_questions: {
        key: 'merchant_questions',
        label: 'Your own questions',
        inheritsFrom: 'studies',
        requiresConnection: true,
        defaultGlobalState: 'enabled',
        defaultGlobalReason: 'Merchant-authored study questions are generally available.',
        dependsOn: ['studies'],
    },
    micro_surveys: {
        key: 'micro_surveys',
        label: 'Micro-surveys',
        requiresConnection: true,
        defaultGlobalState: 'coming_soon',
        defaultGlobalReason: 'Micro-surveys are not generally available yet.',
        dependsOn: ['pixel_events'],
    },
    product_edit_apply: {
        key: 'product_edit_apply',
        label: 'Product-edit apply',
        inheritsFrom: 'studies',
        requiresConnection: true,
        defaultGlobalState: 'enabled',
        defaultGlobalReason: 'Product-edit apply capability is available for eligible plans.',
        dependsOn: ['studies'],
    },
    integration_shopify: {
        key: 'integration_shopify',
        label: 'Shopify integration',
        requiresConnection: false,
        defaultGlobalState: 'enabled',
        defaultGlobalReason: 'Shopify integration is generally available for eligible plans.',
        dependsOn: [],
    },
    integration_posthog: {
        key: 'integration_posthog',
        label: 'PostHog integration',
        requiresConnection: false,
        defaultGlobalState: 'coming_soon',
        defaultGlobalReason: 'PostHog integration is not generally available yet.',
        dependsOn: [],
    },
    integration_google_search_console: {
        key: 'integration_google_search_console',
        label: 'Google Search Console integration',
        requiresConnection: false,
        defaultGlobalState: 'coming_soon',
        defaultGlobalReason: 'Google Search Console integration is not generally available yet.',
        dependsOn: [],
    },
    // #3242 slice E1 fix: registered (shipped in code, `visibility: 'public'`) so the subscription
    // reaches the same authorization/discovery/connection management APIs every other provider
    // uses, but held at `coming_soon` the same way `integration_posthog` and
    // `integration_google_search_console` shipped before their launch — no plan grants it yet, so
    // `assertIntegrationProviderEnabled` refuses every organization until a plan or global-state
    // change turns it on.
    integration_meta_ads: {
        key: 'integration_meta_ads',
        label: 'Meta Ads integration',
        requiresConnection: false,
        defaultGlobalState: 'coming_soon',
        defaultGlobalReason: 'Meta Ads connections are not generally available yet.',
        dependsOn: [],
    },
    integration_competitor_catalog: {
        key: 'integration_competitor_catalog',
        label: 'Competitor catalog integration',
        requiresConnection: false,
        defaultGlobalState: 'coming_soon',
        defaultGlobalReason: 'Competitor catalog subscriptions are not generally available yet.',
        dependsOn: [],
    },
    research_cards: {
        key: 'research_cards',
        label: 'Case study insights',
        requiresConnection: true,
        defaultGlobalState: 'disabled',
        defaultGlobalReason: 'Case study insights await release approval.',
        dependsOn: [],
    },
    // requiresConnection: true because market_discovery_brief.connection_id is NOT NULL and
    // consumes an existing Shopify connection (unlike integration_shopify, which establishes one).
    market_discovery: {
        key: 'market_discovery',
        label: 'Market discovery',
        requiresConnection: true,
        defaultGlobalState: 'coming_soon',
        defaultGlobalReason: 'Market discovery is admin-CLI-only while the storage and execution model is being proven (#3272).',
        dependsOn: [],
    },
};
export const defaultShopifyFeatureGlobalStates = shopifyFeatureKeys.map((key) => ({
    featureKey: key,
    state: featureMetadata[key].defaultGlobalState,
    reason: featureMetadata[key].defaultGlobalReason,
}));
/** Resolves effective feature access without reading persistence or external services. */
export function resolveFeatureEntitlements(input) {
    const context = entitlementContextSchema.parse(input.context);
    const globalStates = input.globalStates.map((state) => featureGlobalStateSchema.parse(state));
    const planGrants = input.planGrants.map((grant) => featurePlanGrantSchema.parse(grant));
    const overrides = input.overrides.map((override) => featureOverrideSchema.parse(override));
    const resolved = Object.fromEntries(shopifyFeatureKeys.map((key) => [
        key,
        resolveSingleFeature({
            key,
            context,
            globalStates,
            planGrants,
            overrides,
            resolved: {},
        }),
    ]));
    for (const key of shopifyFeatureKeys) {
        resolved[key] = resolveSingleFeature({
            key,
            context,
            globalStates,
            planGrants,
            overrides,
            resolved,
        });
    }
    return { context, features: resolved };
}
/** Builds the fail-closed Shopify baseline used before async entitlement reads complete. */
export function buildDefaultShopifyEntitlementSnapshot(input) {
    return resolveFeatureEntitlements({
        context: {
            orgId: input.orgId ?? null,
            productSurface: 'shopify_integration',
            connectionStatus: input.connectionStatus,
            shopDomain: input.shopDomain ?? null,
            connectionId: input.connectionId ?? null,
        },
        globalStates: defaultShopifyFeatureGlobalStates,
        planGrants: input.connectionStatus === 'active'
            ? [
                { featureKey: 'synthetic_audience', grantState: 'included' },
                { featureKey: 'integration_shopify', grantState: 'included' },
            ]
            : [],
        overrides: [],
    });
}
/** Builds a stable fail-closed snapshot when persisted entitlement inputs cannot be trusted. */
export function buildResolutionErrorEntitlementSnapshot(input) {
    const context = entitlementContextSchema.parse(input);
    const features = Object.fromEntries(shopifyFeatureKeys.map((key) => [
        key,
        makeEntitlement(key, 'unavailable', ['resolution_error'], 'entitlement_resolution_error'),
    ]));
    return { context, features };
}
/** Builds the Shopify-shaped resolution-error snapshot retained by existing Shopify adapters. */
export function buildResolutionErrorShopifyEntitlementSnapshot(input) {
    return buildResolutionErrorEntitlementSnapshot({
        orgId: input.orgId ?? null,
        productSurface: 'shopify_integration',
        connectionStatus: input.connectionStatus ?? 'missing',
        shopDomain: input.shopDomain ?? null,
        connectionId: input.connectionId ?? null,
    });
}
/** Checks whether a resolved feature can be used at a server boundary. */
export function isFeatureEnabled(snapshot, key) {
    return snapshot.features[key].state === 'enabled';
}
function resolveSingleFeature(input) {
    const metadata = featureMetadata[input.key];
    const globalState = input.globalStates.find((state) => state.featureKey === input.key);
    const override = findMatchingOverride(input);
    const planGrant = findPlanGrant(input);
    const dependencyBlocker = findDependencyBlocker(input);
    if (metadata.requiresConnection &&
        input.context.productSurface === 'shopify_integration' &&
        input.context.connectionStatus !== 'active') {
        return makeEntitlement(input.key, 'unavailable', ['missing_connection'], 'missing_connection');
    }
    if (globalState?.state === 'disabled') {
        return makeEntitlement(input.key, 'disabled', ['global'], 'global_disabled');
    }
    if (globalState?.state === 'coming_soon' && override?.overrideState !== 'enabled') {
        return makeEntitlement(input.key, 'coming_soon', ['global'], 'global_coming_soon');
    }
    if (override) {
        if (override.overrideState === 'enabled' && dependencyBlocker)
            return dependencyBlocker;
        return makeEntitlement(input.key, override.overrideState === 'enabled' ? 'enabled' : 'disabled', ['override'], override.overrideState === 'enabled' ? 'override_enabled' : 'override_disabled');
    }
    if (planGrant?.grantState === 'excluded') {
        return makeEntitlement(input.key, 'disabled', ['plan'], 'plan_excluded');
    }
    if (metadata.inheritsFrom && input.resolved[metadata.inheritsFrom]?.state === 'enabled') {
        if (dependencyBlocker)
            return dependencyBlocker;
        return makeEntitlement(input.key, 'enabled', ['plan'], `inherits_${metadata.inheritsFrom}`);
    }
    if (planGrant?.grantState === 'included') {
        if (dependencyBlocker)
            return dependencyBlocker;
        return makeEntitlement(input.key, 'enabled', ['plan'], 'plan_granted');
    }
    if (dependencyBlocker)
        return dependencyBlocker;
    return makeEntitlement(input.key, 'disabled', [], 'not_granted');
}
function makeEntitlement(key, state, sources, reasonCode) {
    return {
        key,
        label: featureMetadata[key].label,
        state,
        sources,
        reasonCode,
        dependsOn: featureMetadata[key].dependsOn,
    };
}
function findPlanGrant(input) {
    return input.planGrants.find((grant) => grant.featureKey === input.key);
}
function findMatchingOverride(input) {
    return input.overrides.find((override) => {
        if (override.featureKey !== input.key)
            return false;
        if (override.scopeType === 'org')
            return override.scopeId === input.context.orgId;
        return override.scopeId === input.context.shopDomain;
    });
}
function findDependencyBlocker(input) {
    for (const dependency of featureMetadata[input.key].dependsOn) {
        const resolvedDependency = input.resolved[dependency];
        if (resolvedDependency && resolvedDependency.state !== 'enabled') {
            return makeEntitlement(input.key, 'unavailable', ['dependency'], `dependency_${dependency}_${resolvedDependency.state}`);
        }
    }
    return null;
}
//# sourceMappingURL=index.js.map